import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { cn } from '@/utils'

interface ScrollFadeProps {
  children: ReactNode
  /** Classes for the scroll viewport, e.g. a `max-h-*` cap. */
  className?: string
  /** Classes for the outer wrapper, e.g. border + rounded. */
  containerClassName?: string
  /** Inline styles for the scroll viewport, e.g. a computed `maxHeight`. */
  style?: CSSProperties
}

// A capped-height scroll area that shows a soft gradient shadow along its
// bottom edge while there is more content below, and hides it once the user
// reaches the end (or when nothing overflows).
export const ScrollFade = ({
  children,
  className,
  containerClassName,
  style,
}: ScrollFadeProps) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(true)

  const syncAtBottom = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const remaining =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
    setAtBottom(remaining <= 1)
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    syncAtBottom()
    const observer = new ResizeObserver(syncAtBottom)
    observer.observe(viewport)
    if (viewport.firstElementChild) {
      observer.observe(viewport.firstElementChild)
    }
    return () => observer.disconnect()
  }, [syncAtBottom])

  return (
    <div className={cn('relative overflow-hidden', containerClassName)}>
      <div
        ref={viewportRef}
        onScroll={syncAtBottom}
        style={style}
        className={cn(
          'overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent',
          className,
        )}
      >
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 z-20 h-10 bg-gradient-to-t from-black/10 to-transparent transition-opacity duration-200',
          atBottom ? 'opacity-0' : 'opacity-100',
        )}
      />
    </div>
  )
}
